import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useEffect, useRef, useState, memo, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { useColorMode, Box, Button, Flex, Text } from "theme-ui";
import { FaCalendarAlt } from 'react-icons/fa';
import { keyframes } from "@emotion/react";
import { DateRangeSelect } from "../../../../forms-component/dateRangeSelect";
import { Inputform } from "../../../../forms-component/InputForm";
import CustomTooltip from "../../../../CustomTooltip";
import { TeamData, TeamManagerData, TeamWorkerData } from "../../../../../types/dashboard.types";
import GenericChartSkeleton from "../../skeleton/GenericChartSkeleton";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const dataLabelPlugin = {
    id: 'dataLabelPlugin',
    afterDatasetsDraw(chart: { ctx: CanvasRenderingContext2D; data: { datasets: any[] }; getDatasetMeta: (i: number) => any }) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset: any, i: number) => {
            const isDark = dataset.isDarkMode !== false;

            const meta = chart.getDatasetMeta(i);
            meta.data.forEach((bar: any, index: number) => {
                const value = dataset.data[index];
                if (value === null || value === undefined) return;
                if (bar.hidden) return;

                const x = bar.x;
                const y = bar.y - 15;

                let barColor = '#ffffff';

                if (!isDark) {
                    if (bar.options && bar.options.backgroundColor) {
                        barColor = bar.options.backgroundColor;
                    } else if (Array.isArray(dataset.backgroundColor)) {
                        barColor = dataset.backgroundColor[index % dataset.backgroundColor.length];
                    } else if (typeof dataset.backgroundColor === 'string') {
                        barColor = dataset.backgroundColor;
                    } else {
                        barColor = '#000000';
                    }
                }

                ctx.fillStyle = barColor;
                ctx.font = 'bold 11px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(String(value), x, y);
            });
        });
    }
};


// ... (existing imports)


interface TeamWorkloadChartProps {
    teamName: string;
    teamData: TeamData;
    teamColorSet?: string;
    loading?: boolean;
    dateRange: string;
    setDateRange: (value: string, viewMode: 'worker' | 'manager') => void;
    timeMode: 'remaining' | 'estimation';
    setTimeMode: (mode: 'remaining' | 'estimation') => void;
}

const TeamWorkloadChart = memo(({
    teamName,
    teamData,
    teamColorSet = "default",
    loading = false,
    dateRange,
    setDateRange,
    timeMode,
    setTimeMode
}: TeamWorkloadChartProps) => {
    const { workers = [], managers = [] } = teamData || {};

    const workerLabels = useMemo(() => workers.map(w => w.name), [workers]);
    const workerActiveData = useMemo(() => workers.map(w => w.activeProjects), [workers]);
    const workerHoursData = useMemo(() => workers.map(w => timeMode === 'estimation' ? (w.estimationHours ?? w.estimateHours ?? w.remainingHours) : w.remainingHours), [workers, timeMode]);

    const managerLabels = useMemo(() => managers.map(m => m.name), [managers]);
    const managerActiveData = useMemo(() => managers.map(m => m.activeProjects), [managers]);
    const managerPendingData = useMemo(() => managers.map(m => m.pendingReview), [managers]);
    const [viewMode, setViewMode] = useState<'worker' | 'manager'>('worker');
    const [colorMode] = useColorMode();
    const isDark = colorMode === 'dark';
    const graphlabelColor = colorMode === 'light' ? "#453521" : "#dadada";
    const borderColor = colorMode === 'light' ? "#e5e7eb" : "#2e354d";

    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    const shimmer = keyframes`
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    `;

    const shimmerBase = isDark ? "#2e354d" : "#e0e0e0";
    const shimmerHighlight = isDark ? "#3a425e" : "#f5f5f5";

    const commonShimmer = {
        background: `linear-gradient(to right, ${shimmerBase} 4%, ${shimmerHighlight} 25%, ${shimmerBase} 36%)`,
        backgroundSize: "1000px 100%",
        animation: `${shimmer} 2s infinite linear`,
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const title = viewMode === 'worker'
        ? `${teamName} Team Workload (Projects & Hours)`
        : `${teamName} Team Workload (Managers)`;


    let activeColor = "#facc15";
    let secondaryColorTyped = "#4ade80";

    if (viewMode === 'worker') {
        activeColor = "#eab308";

    } else {

        if (teamColorSet === 'mechanical') { activeColor = "#a3e635"; secondaryColorTyped = "#22c55e"; }
        if (teamColorSet === 'electrical') { activeColor = "#fbbf24"; secondaryColorTyped = "#f59e0b"; }
        if (teamColorSet === 'plumbing') { activeColor = "#67e8f9"; secondaryColorTyped = "#3b82f6"; }
    }

    const formatLabel = (name: string) => {
        if (!name) return "";
        const parts = name.trim().split(/\s+/);
        if (parts.length > 1) {
            return `${parts[0]} ${parts[parts.length - 1][0]}.`;
        }
        return name;
    };

    const labels = (viewMode === 'worker' ? workerLabels : managerLabels).map(formatLabel);
    const activeData = viewMode === 'worker' ? workerActiveData : managerActiveData;
    const secondaryData = viewMode === 'worker' ? workerHoursData : managerPendingData;

    const datasets = [
        {
            label: 'Active Projects',
            data: activeData,
            backgroundColor: activeColor,
            barThickness: 50,
            borderRadius: { topLeft: 5, topRight: 5 },
            categoryPercentage: 0.8,
            order: 1,
            isDarkMode: isDark
        }
    ];

    if (viewMode === 'worker') {
        datasets.push({
            label: timeMode === 'estimation' ? 'Estimate Hours' : 'Remaining Hours',
            data: secondaryData,
            backgroundColor: secondaryData.map((h: number | string) => (timeMode === 'estimation' ? "#4ade80" : (Number(h) > 40 ? "#ef4444" : "#4ade80"))),
            barThickness: 50,
            borderRadius: { topLeft: 5, topRight: 5 },
            categoryPercentage: 0.8,
            order: 2,
            isDarkMode: isDark
        } as any);
    } else {
        datasets.push({
            label: 'Pending Review',
            data: secondaryData,
            backgroundColor: secondaryColorTyped,
            barThickness: 50,
            borderRadius: { topLeft: 5, topRight: 5 },
            categoryPercentage: 0.8,
            order: 2,
            isDarkMode: isDark
        } as any);
    }

    const data = {
        labels,
        datasets
    };

    const chartMinWidth = labels.length * 120;

    const maxActive = Math.max(...(activeData as number[]), 0);
    const maxSecondary = Math.max(...(secondaryData as number[]), 0);
    const overallMax = Math.max(maxActive, maxSecondary);
    const yAxisMax = overallMax + Math.ceil(overallMax * 0.15) + 1;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: isDark ? '#20283c' : '#ffffff',
                titleColor: isDark ? '#ffffff' : '#20283c',
                bodyColor: isDark ? '#cbd5e1' : '#475569',
                borderColor: borderColor,
                borderWidth: 1,
                padding: 12,
                boxPadding: 4,
                usePointStyle: true,
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                suggestedMax: yAxisMax,
                grid: { color: isDark ? '#2e354d' : '#eee' },
                ticks: { color: graphlabelColor }
            },
            x: {
                grid: { display: false },
                ticks: {
                    color: graphlabelColor,
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: false,
                }
            }
        }
    };

    const cardBg = colorMode === 'light' ? "#ffffff" : "#2e354db8";
    const titleColor = colorMode === 'light' ? "#453521" : "#ffffff";

    return (
        <Box sx={{ bg: cardBg, p: 3, borderRadius: 8, mb: 3 }}>
            <Flex sx={{ justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                <Flex sx={{ alignItems: 'center' }}>
                    <Text sx={{ color: titleColor, fontSize: 2, fontWeight: 'bold' }}>{title}</Text>
                    {viewMode === 'worker' && (
                        <Flex sx={{ alignItems: 'center', ml: 3, gap: 2 }}>
                            <CustomTooltip id="projects-tooltip" title="Active Projects">
                                <Box sx={{ width: 'auto', cursor: "pointer" }}>
                                    {loading || teamData.totalTask === null ? (
                                        <Box sx={{ width: '50px', height: '30px', borderRadius: '2px', ...commonShimmer }} />
                                    ) : (
                                        <Inputform
                                            label=""
                                            values={teamData.totalTask}
                                            Name="totalProjects"
                                            Placeholder="0"
                                            disabled={true}
                                            wrapperClassName="d-flex align-items-center"
                                            sx={{
                                                color: titleColor,
                                                fontSize: 1,
                                                fontWeight: 'bold',
                                                whiteSpace: 'nowrap',
                                                mb: 0,
                                                "&.form-textcontrol": {
                                                    width: '80px',
                                                    height: '30px',
                                                    bg: isDark ? '#20283c' : '#f3f4f6',
                                                    borderRadius: '2px !important',
                                                    px: 2,
                                                    py: 0,
                                                    border: '1px solid',
                                                    borderColor: borderColor,
                                                    fontWeight: 'normal',
                                                    textAlign: 'center',
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                            </CustomTooltip>
                            <CustomTooltip id="hours-tooltip" title={timeMode === 'estimation' ? "Estimate Hours" : "Remaining Hours"}>
                                <Box sx={{ width: 'auto', cursor: "pointer" }}>
                                    {loading || teamData.totalRemainingTime === null ? (
                                        <Box sx={{ width: '70px', height: '30px', borderRadius: '2px', ...commonShimmer }} />
                                    ) : (
                                        <Inputform
                                            label=""
                                            values={timeMode === 'estimation' ? (teamData.totalEstimationTime ?? teamData.totalEstimateTime ?? teamData.totalRemainingTime) : teamData.totalRemainingTime}
                                            Name="totalHours"
                                            Placeholder="0"
                                            disabled={true}
                                            wrapperClassName="d-flex align-items-center"
                                            sx={{
                                                color: titleColor,
                                                fontSize: 1,
                                                fontWeight: 'bold',
                                                whiteSpace: 'nowrap',
                                                mb: 0,
                                                "&.form-textcontrol": {
                                                    width: '80px',
                                                    height: '30px',
                                                    bg: isDark ? '#20283c' : '#f3f4f6',
                                                    borderRadius: '2px !important',
                                                    px: 2,
                                                    py: 0,
                                                    border: '1px solid',
                                                    borderColor: borderColor,
                                                    fontWeight: 'normal',
                                                    textAlign: 'center',
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                            </CustomTooltip>
                        </Flex>
                    )}
                </Flex>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <Flex sx={{ alignItems: 'center', gap: 2 }}>
                        {dateRange && (
                            <Button
                                onClick={() => setDateRange("", viewMode)}
                                sx={{
                                    bg: 'transparent',
                                    color: 'primary',
                                    border: '1px solid',
                                    borderColor: 'primary',
                                    px: 2,
                                    py: 0,
                                    fontSize: 0,
                                    height: '24px',
                                    cursor: 'pointer',
                                    borderRadius: 4,
                                    '&:hover': { bg: 'primary', color: 'white' }
                                }}
                            >
                                Clear
                            </Button>
                        )}
                        <Box sx={{ position: 'relative' }} ref={calendarRef}>
                            <Box
                                sx={{
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    p: 1,
                                    borderRadius: '4px',
                                    '&:hover': { bg: loading ? 'transparent' : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    opacity: loading ? 0.5 : 1
                                }}
                                onClick={() => {
                                    if (!loading) {
                                        setIsCalendarOpen(!isCalendarOpen);
                                    }
                                }}
                            >
                                <FaCalendarAlt size={16} color={titleColor} />
                            </Box>
                            {isCalendarOpen && (
                                <Box sx={{ position: 'absolute', top: '100%', right: 0, zIndex: 10, mt: 1, bg: isDark ? 'background' : 'white', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                                    <DateRangeSelect
                                        onChange={(value: string) => {
                                            if (value && value.includes(" to ")) {
                                                setDateRange && setDateRange(value, viewMode);
                                                setIsCalendarOpen(false);
                                            }
                                        }}
                                        values={dateRange || ""}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Flex sx={{ gap: 0, border: `1px solid ${borderColor}`, borderRadius: 4, overflow: 'hidden' }}>
                            <Button
                                onClick={() => setViewMode('worker')}
                                sx={{
                                    borderRadius: 0,
                                    bg: viewMode === 'worker' ? 'primary' : 'transparent',
                                    color: viewMode === 'worker' ? 'white' : 'gray',
                                    fontSize: 1,
                                    py: 1,
                                    px: 2,
                                    cursor: 'pointer',
                                    '&:hover': { bg: viewMode === 'worker' ? 'primary' : borderColor }
                                }}
                            >
                                Workers
                            </Button>
                            <Button
                                onClick={() => setViewMode('manager')}
                                sx={{
                                    borderRadius: 0,
                                    bg: viewMode === 'manager' ? 'primary' : 'transparent',
                                    color: viewMode === 'manager' ? 'white' : 'gray',
                                    fontSize: 1,
                                    py: 1,
                                    px: 2,
                                    cursor: 'pointer',
                                    '&:hover': { bg: viewMode === 'manager' ? 'primary' : borderColor }
                                }}
                            >
                                Managers
                            </Button>
                        </Flex>
                        {viewMode === 'worker' && (
                            <Flex sx={{ gap: 0, border: `1px solid ${borderColor}`, borderRadius: 4, overflow: 'hidden', ml: 2 }}>
                                <Button
                                    onClick={() => setTimeMode('estimation')}
                                    sx={{
                                        borderRadius: 0,
                                        bg: timeMode === 'estimation' ? 'primary' : 'transparent',
                                        color: timeMode === 'estimation' ? 'white' : 'gray',
                                        fontSize: 1,
                                        py: 1,
                                        px: 2,
                                        cursor: 'pointer',
                                        '&:hover': { bg: timeMode === 'estimation' ? 'primary' : borderColor }
                                    }}
                                >
                                    Estimate Hours
                                </Button>
                                <Button
                                    onClick={() => setTimeMode('remaining')}
                                    sx={{
                                        borderRadius: 0,
                                        bg: timeMode === 'remaining' ? 'primary' : 'transparent',
                                        color: timeMode === 'remaining' ? 'white' : 'gray',
                                        fontSize: 1,
                                        py: 1,
                                        px: 2,
                                        cursor: 'pointer',
                                        '&:hover': { bg: timeMode === 'remaining' ? 'primary' : borderColor }
                                    }}
                                >
                                    Remaining Hours
                                </Button>
                            </Flex>
                        )}
                    </Flex>
                    {/* Custom Legend */}
                    <Flex sx={{ gap: 3, alignItems: 'center' }}>
                        <Flex sx={{ alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 12, height: 12, bg: activeColor, borderRadius: '2px' }} />
                            <Text sx={{ color: 'gray', fontSize: 1 }}>Active Projects</Text>
                        </Flex>
                        {viewMode === 'worker' ? (
                            <>
                                <Flex sx={{ alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ width: 12, height: 12, bg: '#4ade80', borderRadius: '2px' }} />
                                    <Text sx={{ color: 'gray', fontSize: 1 }}>{timeMode === 'estimation' ? 'Estimate Hours' : 'Remaining Hours'}</Text>
                                </Flex>
                                {timeMode === 'remaining' && (
                                    <Flex sx={{ alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ width: 12, height: 12, bg: '#ef4444', borderRadius: '2px' }} />
                                        <Text sx={{ color: 'gray', fontSize: 1 }}>Overloaded ({'>'}40h)</Text>
                                    </Flex>
                                )}
                            </>
                        ) : (
                            <Flex sx={{ alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: 12, height: 12, bg: secondaryColorTyped, borderRadius: '2px' }} />
                                <Text sx={{ color: 'gray', fontSize: 1 }}>Pending Review</Text>
                            </Flex>
                        )}
                    </Flex>
                </Box>
            </Flex>
            <Box sx={{ width: '100%', overflowX: 'auto', pb: 2 }}>
                <Box sx={{ height: 300, width: '100%', minWidth: loading ? '100%' : chartMinWidth }}>
                    {loading ? (
                        <GenericChartSkeleton height="300px" />
                    ) : (
                        /* @ts-ignore */
                        <Bar options={options} data={data} plugins={[dataLabelPlugin]} />
                    )}
                </Box>
            </Box >
        </Box >
    );
});

export default TeamWorkloadChart;
